import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import {
  addSectionStart,
  addSectionSuccess,
  addSectionFailure,
  fetchSectionsSuccess,
  updateSectionStart,
  updateSectionFailure,
} from "../../../core/data/redux/sectionSlice";
import {
  addSectionApi,
  fetchSectionApi,
  updateSectionApi,
  deleteSectionApi
} from "../../../core/data/services/sectionService";
import { section } from "../../../core/data/types/section";
import Table from "../../../core/common/dataTable/index";
import {
  // activeList,
  schoolClassId
} from "../../../core/common/selectoption/selectoption";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import { TableData } from "../../../core/data/interface";
import { Link } from "react-router-dom";
import TooltipOption from "../../../core/common/tooltipOption";
import { all_routes } from "../../router/all_routes";
import { fetchTeachersApi } from "../../../core/data/services/teacherService";
import { fetchteachersSuccess } from "../../../core/data/redux/teacherSlice";
import SuccessModal from "../../../core/common/successModal";
import ErrorModal from "../../../core/common/ErrorModal";
import { getUserDetails } from "../../../lib/getUserDetails";
import { ExportColumn, exportToExcel, exportToPDF } from "../../../utils/exportUtils";
import { fetchclassesApi } from "../../../core/data/services/classService";
import { fetchclassesSuccess } from "../../../core/data/redux/classSlice";

// Extended section interface to include populated schoolClass
interface SectionWithClass extends section {
  schoolClass?: {
    id: number;
    name: string;
    academicYearId: number;
    classKey: string;
    schoolId: number;
    isDeleted: boolean;
    createdAt: string | null;
    updatedAt: string | null;
    createdBy: string | null;
    updatedBy: string | null;
  };
}

const Sections = () => {
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();
  const { sections: sectionList } = useSelector((state: RootState) => state.sections) as { sections: SectionWithClass[] };
  const { classes: classList } = useSelector((state: RootState) => state.classes);

  const [sectionName, setSectionName] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<Option | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<Option | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editSectionId, setEditSectionId] = useState<number | null>(null);
  const [, setIsLoading] = useState(true);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Loading state for operations
  const [operationLoading, setOperationLoading] = useState<boolean>(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteSectionId, setDeleteSectionId] = useState<number | null>(null);
  const [deleteSectionName, setDeleteSectionName] = useState<string>("");

  const userData = getUserDetails();
  const schoolId = userData?.[0]?.relatedEntity?.schools?.id;

  const userDetail = localStorage.getItem('userDetail');
  const parsedUserDetailData = JSON.parse(userDetail || "null");

  // ADDED: Function to refresh data from API
  const refreshData = async () => {
    try {
      const [updatedSection, updatedTeachers, updatedClasses] = await Promise.all([
        fetchSectionApi(),
        fetchTeachersApi(),
        fetchclassesApi(),
      ]);

      dispatch(fetchSectionsSuccess(updatedSection));
      dispatch(fetchteachersSuccess(updatedTeachers));
      dispatch(fetchclassesSuccess(updatedClasses));
    } catch (error: any) {
      console.error("Error refreshing data:", error);
    }
  };



  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        const [addedSections, addedTeachers, addedClasses] = await Promise.all([
          fetchSectionApi(),
          fetchTeachersApi(),
          fetchclassesApi(),
        ]);

        if (!isCancelled) {
          dispatch(fetchSectionsSuccess(addedSections));
          dispatch(fetchteachersSuccess(addedTeachers));
          dispatch(fetchclassesSuccess(addedClasses));
          setIsLoading(false);
        }
      } catch (error: any) {
        if (!isCancelled) {
          console.error("Error fetching data:", error);
          const message = getErrorMessage(error);
          setErrorMessage(message);
          setShowErrorModal(true);
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [dispatch]);

  // Fetch teachers data from the Redux store
  const teachers = useSelector((state: RootState) => state.teachers.teachers);

  const teacherOptions: Option[] = teachers.map((teacher) => ({
    value: String(teacher.id),
    label: teacher.firstName,
  }));

  // Helper function to extract error messages
  const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    if (error?.response?.data?.details) {
      return error.response.data.details;
    }

    if (error?.message && error.message !== "Request failed with status code 400") {
      return error.message;
    }

    return "An unexpected error occurred. Please try again.";
  };

  // Sort sections by class name and then by section name
  const sortedTableData = useMemo(() => {
    if (!sectionList || sectionList.length === 0) return [];

    return [...sectionList].sort((a: SectionWithClass, b: SectionWithClass) => {
      // First sort by class name
      const classA = a.schoolClass?.name || '';
      const classB = b.schoolClass?.name || '';

      if (classA !== classB) {
        return classA.localeCompare(classB);
      }

      // If classes are the same, sort by section name
      return a.name.localeCompare(b.name);
    });
  }, [sectionList]);

  const handleClassChange = (selected: Option | null) => {
    setSelectedClass(selected);
  };

  const handleTeacherChange = (selected: Option | null) => {
    setSelectedTeacher(selected);
  };

  const handleAddSection = async (e: React.FormEvent) => {
    e.preventDefault();
    setOperationLoading(true);

    // Reset modals
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    const classOption = classList?.find(cls => cls.name === String(selectedClass?.value));

    if (!classOption?.id) {
      setErrorMessage("Class not found");
      setShowErrorModal(true);
      setOperationLoading(false);
      return;
    }

    const newSection: Omit<section, "id"> = {
      name: sectionName.trim(),
      schoolClassId: classOption?.id,
      schoolId: schoolId, // Replace with actual school ID
      totalEnrollment: null,
      mdmEnrollment: null,
      teacherId: selectedTeacher ? parseInt(selectedTeacher.value) : null,
      isDeleted: false,
      createdBy: parsedUserDetailData?.id, // Replace with actual user ID
      updatedBy: parsedUserDetailData?.id,
      createdAt: null,
      updatedAt: null,
    };

    dispatch(addSectionStart());
    
    try {
      const addedSection = await addSectionApi(newSection);
      dispatch(addSectionSuccess(addedSection));
      // FIXED: Refresh data to get the latest state from server
      await refreshData();
      setSuccessMessage("Section added successfully!");
      setShowSuccessModal(true);
      resetForm();
    } catch (error: any) {
      console.error("Error adding section:", error);
      const message = getErrorMessage(error);
      dispatch(addSectionFailure(message));
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEditSection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSectionId) return;

    setOperationLoading(true);

    // Reset modals
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    // Find the original section data
    const originalSection = sectionList.find((sec: SectionWithClass) => sec.id === editSectionId);
    if (!originalSection) {
      setErrorMessage("Section not found for editing");
      setShowErrorModal(true);
      setOperationLoading(false);
      return;
    }

    // Prepare the complete section object with all required fields
    const updatedSection: section = {
      ...originalSection,
      id: editSectionId,
      name: sectionName.trim(),
      schoolClassId: selectedClass ? Number(selectedClass.value) : originalSection.schoolClassId,
      teacherId: selectedTeacher ? Number(selectedTeacher.value) : Number(originalSection?.teacherId) || null,
      updatedBy: parsedUserDetailData?.id, // Replace with actual user ID
      updatedAt: new Date().toISOString(),
    };
    
    dispatch(updateSectionStart());
    try {
      const updated = await updateSectionApi(updatedSection);

      // Manual state update to ensure immediate UI reflection
      const updatedSectionList = sectionList.map((sec: SectionWithClass) =>
        sec.id === editSectionId ? updated : sec
      );
      dispatch(fetchSectionsSuccess(updatedSectionList));
      // Also dispatch the specific update action

      dispatch(addSectionSuccess(updated));
      // FIXED: Refresh data to get the latest state from server
      await refreshData();
      setSuccessMessage("Section updated successfully!");
      setShowSuccessModal(true);
      resetForm();
    } catch (error: any) {
      console.error("Error updating section:", error);
      const message = getErrorMessage(error);
      dispatch(updateSectionFailure(message));
      setErrorMessage(message);
      setShowErrorModal(true);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!deleteSectionId) return;

    setOperationLoading(true);

    // Reset modals
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    try {
      await deleteSectionApi(deleteSectionId);
      // Remove the deleted section from the local state
      const updatedSectionList = sectionList.filter((sec: SectionWithClass) => sec.id !== deleteSectionId);
      dispatch(fetchSectionsSuccess(updatedSectionList));
      // FIXED: Refresh data to get the latest state from server
      await refreshData();
      setSuccessMessage("Section deleted successfully!");
      setShowSuccessModal(true);
      setShowDeleteModal(false);
      setDeleteSectionId(null);
      setDeleteSectionName("");
    } catch (error: any) {
      console.error("Error deleting section:", error);
      const message = getErrorMessage(error);
      setErrorMessage(message);
      setShowErrorModal(true);
      setShowDeleteModal(false);
    } finally {
      setOperationLoading(false);
    }
  };

  const resetForm = () => {
    setSectionName("");
    setSelectedClass(null);
    setSelectedTeacher(null);
    setEditMode(false);
    setEditSectionId(null);
  };

  const handleEditClick = (sectionData: SectionWithClass) => {
    setEditMode(true);
    setEditSectionId(sectionData.id);
    setSectionName(sectionData.name);

    // Set selected class
    const classOption = schoolClassId.find(cls => cls.value === String(sectionData.schoolClass?.name));
    setSelectedClass(classOption || null);

    // Set selected teacher
    const teacher = teachers.find((t) => t.id === Number(sectionData.teacherId));
    if (teacher) {
      setSelectedTeacher({
        value: String(teacher.id),
        label: teacher.firstName,
      });
    } else {
      setSelectedTeacher(null);
    }
  };

  const handleDeleteClick = (sectionData: SectionWithClass) => {
    setDeleteSectionId(sectionData.id);
    setDeleteSectionName(sectionData.name);
    setShowDeleteModal(true);
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCloseSuccessModal = async () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteSectionId(null);
    setDeleteSectionName("");
  };

  const exportColumns: ExportColumn[] = [
    {
      title: "Unique ID",
      dataIndex: "id",
    },
    {
      title: "Class",
      dataIndex: "schoolClass",
    },
    {
      title: "Section Name",
      dataIndex: "name",
    },
    {
      title: "Class & Section",
      dataIndex: "schoolClassId",

    },
    {
      title: "Teacher",
      dataIndex: "teacherId",
    },
    {
      title: "Total Enrollment",
      dataIndex: "totalEnrollment",
    }
  ];

  const handleExportPDF = () =>
    exportToPDF(sortedTableData, exportColumns, "Sections.pdf");
  const handleExportExcel = () =>
    exportToExcel(sortedTableData, exportColumns, "Sections.xlsx");


  const route = all_routes;

  const columns = [
    {
      title: "Unique ID",
      dataIndex: "id",
      sorter: (a: TableData, b: TableData) => a?.id - b?.id,
      render: (_: any, record: any) => <span>{"UNIID" + record.id}</span>,
    },

    {
      title: "Class",
      dataIndex: "schoolClass",
      render: (schoolClass: any) => {
        return schoolClass ? schoolClass.name : <span className="text-muted">Not Assigned</span>;
      },
      sorter: (a: any, b: any) => {
        const classA = a.schoolClass?.name || '';
        const classB = b.schoolClass?.name || '';
        return classA.localeCompare(classB);
      },
    },
  {
  title: "Section Name",
  dataIndex: "name",
  sorter: (a: TableData, b: TableData) => {
    // Handle null/undefined cases and ensure case-insensitive sorting
    const nameA = a.name?.toLowerCase() || "";
    const nameB = b.name?.toLowerCase() || "";
    // Compare for ascending order; table library handles descending
    return nameA.localeCompare(nameB);
  },
},
    {
      title: "Teacher",
      dataIndex: "teacherId",
      render: (teacherId: number | string | null) => {
        if (!teacherId) return <span className="text-muted">Not Assigned</span>;

        // Convert teacherId to number for comparison since it might be a string
        const numericTeacherId = typeof teacherId === 'string' ? parseInt(teacherId) : teacherId;
        const teacher = teachers.find(t => t.id === numericTeacherId);

        return teacher ?
          `${ teacher.firstName } ${ teacher.lastName }` :
          <span className="text-muted">Unknown</span>;
      },
    },
    {
      title: "Total Enrollment",
      dataIndex: "totalEnrollment",
      render: (enrollment: number | null) => enrollment || <span className="text-muted">-</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: SectionWithClass) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#add_section"
                    onClick={() => handleEditClick(record)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteClick(record);
                    }}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Sections List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.HeadmasterDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Sections
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel} />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_section"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Section
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Sections List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Sections List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={schoolClassId}
                              />
                            </div>
                          </div>
                          {/* <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={activeList}
                              />
                            </div>
                          </div> */}
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link to="#" className="btn btn-light me-3">
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by Class
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1 active">
                        Class A-Z
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Class Z-A
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Section A-Z
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table columns={columns} dataSource={sortedTableData} Selection={true} />
            </div>
          </div>
          {/* /Sections List */}
        </div>
      </div>

      {/* Add/Edit Section Modal */}
      <div className="modal fade" id="add_section">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{editMode ? "Edit Section" : "Add Section"}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={editMode ? handleEditSection : handleAddSection}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Section Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={sectionName}
                        onChange={(e) => setSectionName(e.target.value)}
                        placeholder="Enter section name (e.g., A, B, C)"
                        required
                        // disabled={editMode}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Class</label>
                      <CommonSelect
                        className="select"
                        options={schoolClassId}
                        defaultValue={selectedClass}
                        onChange={handleClassChange}
                        // isDisabled={editMode}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Teacher (Optional)</label>
                      <CommonSelect
                        className="select"
                        options={teacherOptions}
                        defaultValue={selectedTeacher}
                        onChange={handleTeacherChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  disabled={operationLoading}
                >
                  {operationLoading
                    ? editMode
                      ? "Saving..."
                      : "Adding..."
                    : editMode
                      ? "Save Changes"
                      : "Add Section"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Delete Section</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={handleCloseDeleteModal}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <div className="mb-3">
                    <i className="ti ti-trash-x text-danger" style={{ fontSize: '48px' }}></i>
                  </div>
                  <h5 className="mb-3">Are you sure?</h5>
                  <p className="mb-3">
                    Do you want to delete the section <strong>"{deleteSectionName}"</strong>?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={handleCloseDeleteModal}
                  disabled={operationLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteSection}
                  disabled={operationLoading}
                >
                  {operationLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        message={successMessage}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />
    </div>
  );
};

export default Sections;